import toPairs from 'lodash/toPairs'
import { BigNumber } from '@ethersproject/bignumber'

import { useState, useCallback, useEffect } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useFirebase } from 'modules/analytics/firebase'
import { useTransactionSender } from 'modules/blockChain/hooks/useTransactionSender'
import { useNFCSState } from 'modules/nfcs/hooks/useNFCSState'

import { ContractNFCS } from 'modules/contracts/contracts'
import {
  NFCS_MINT_FORM_OPEN_EVENT,
  NFCS_MODAL_ADD_ADDRESS_EVENT,
  NFCS_MODAL_MINT_CLICK_EVENT,
  NFCS_MODAL_REMOVE_ADDRESS_EVENT,
} from 'modules/analytics/events'
import { NFCSFormStep } from 'modules/nfcs/types'
import { useWeb3Signer } from 'modules/wallet/hooks/useWeb3Signer'

type Args = {
  onTxSubmit?: (txHash: string) => void
}

export function useNFCSFormMintActions({ onTxSubmit }: Args) {
  const { walletAddress, library, chainId } = useWeb3()
  const { signTypedData } = useWeb3Signer()
  const {
    signs,
    signedAddresses,
    currentFormStep,
    setSigns,
    handleCurrentFormStepChange,
  } = useNFCSState()
  const { trackEvent } = useFirebase()
  const [isAddressPending, setIsAddressPending] = useState(false)

  const populateMintTx = useCallback(async () => {
    const addressSignPairs = toPairs(signs).filter(pair => Boolean(pair[1]))
    const resultAddresses = addressSignPairs.map(pair => pair[0])
    const resultSigns = addressSignPairs.map(pair => pair[1] as string)

    const contractNFCS = ContractNFCS.connectWeb3({ library, chainId })

    const args: [string[], string[], string] = [
      resultAddresses,
      resultSigns,
      ContractNFCS.version!,
    ]

    const gasLimit = await contractNFCS.estimateGas
      .mintToken(...args)
      .then(r => r.mul(2))
      .catch(() => BigNumber.from(1000000))

    const populatedTx = await contractNFCS.populateTransaction.mintToken(
      ...args,
      {
        gasLimit,
      },
    )

    return populatedTx
  }, [chainId, library, signs])
  const mintTx = useTransactionSender(populateMintTx)

  const { estimateGas, estimatedGas } = mintTx

  const handleAddAddress = useCallback(async () => {
    if (!walletAddress) return
    setIsAddressPending(true)

    try {
      const domain = {
        name: 'NFCS',
        version: ContractNFCS.version,
      }

      if (!signedAddresses.length) {
        // Add primary address without signature
        setSigns({ [walletAddress]: null })
        handleCurrentFormStepChange(NFCSFormStep.Intermediate)
        setIsAddressPending(false)
      } else if (signs[walletAddress] === null) {
        // sign primary address
        const signature = await signTypedData(
          domain,
          {
            PrimaryAddressSignature: [{ name: 'bundle', type: 'address[]' }],
          },
          {
            bundle: signedAddresses,
          },
        )
        setSigns({ [walletAddress]: signature })
        trackEvent(NFCS_MODAL_ADD_ADDRESS_EVENT, {
          address: walletAddress.substr(1),
        })
      } else {
        const signature = await signTypedData(
          domain,
          {
            SecondaryAddressSignature: [{ name: 'primary', type: 'address' }],
          },
          {
            primary: signedAddresses[0],
          },
        )

        setSigns({ [walletAddress]: signature })
        trackEvent(NFCS_MODAL_ADD_ADDRESS_EVENT, {
          address: walletAddress.substr(1),
        })
      }
    } finally {
      setIsAddressPending(false)
    }
  }, [
    walletAddress,
    signedAddresses,
    signs,
    signTypedData,
    trackEvent,
    setSigns,
    handleCurrentFormStepChange,
  ])

  const handleRemoveAddress = useCallback(
    (address: string) => () => {
      setSigns({ [address]: undefined })
      trackEvent(NFCS_MODAL_REMOVE_ADDRESS_EVENT, {
        address: address.substr(1),
      })
    },
    [setSigns, trackEvent],
  )

  const handleMintClick = useCallback(async () => {
    if (currentFormStep === NFCSFormStep.Intermediate) {
      handleCurrentFormStepChange(NFCSFormStep.Finish)
    } else {
      await mintTx.send()
      trackEvent(NFCS_MODAL_MINT_CLICK_EVENT)
    }
  }, [currentFormStep, mintTx, handleCurrentFormStepChange, trackEvent])

  useEffect(() => {
    trackEvent(NFCS_MINT_FORM_OPEN_EVENT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      currentFormStep === NFCSFormStep.Finish &&
      signs[signedAddresses[0]] &&
      !estimatedGas
    ) {
      estimateGas()
    }
  }, [currentFormStep, estimateGas, estimatedGas, signedAddresses, signs])

  useEffect(() => {
    if (mintTx.isPending && mintTx.tx?.hash) {
      onTxSubmit?.(mintTx.tx.hash)
      handleCurrentFormStepChange(NFCSFormStep.Start)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mintTx.isPending, mintTx.tx?.hash])

  return {
    mintTx,
    handleAddAddress,
    handleRemoveAddress,
    handleMintClick,
    isAddressPending,
  }
}
